import { apiClient } from "@/shared/services/api";

export type NotificationType =
  | "DiscardApprovalRequested"
  | "DiscardApproved"
  | "DiscardRejected"
  | "SowingInstructionRejected"
  | "SowingInstructionAmendmentRequested"
  | "SowingInstructionAmendmentApproved"
  | "SowingInstructionAmendmentRejected"
  | "SowingWindowReminder"
  | "PlannedSowingReminder"
  | "PlannedSowingOverdue"
  | "GerminationOverdue"
  | "DailySowingPlan"
  | "WeeklySowingPlan"
  | "StockDepletionAlert"
  | "StockReceiptPendingApproval"
  | "StockReceiptApproved"
  | "StockReceiptRejected"
  | "CustomerSeedReceiptPendingApproval"
  | "CustomerSeedReceiptApproved"
  | "CustomerSeedReceiptRejected"
  | "SupplierCreationPendingApproval"
  | "SupplierCreationApproved"
  | "SupplierCreationRejected"
  // Order lifecycle (SPEC_DEMO2FEEDBACK_ORDER_LIFECYCLE)
  | "OrderPartialFulfillment"
  | "WalkInSaleCompleted"
  | "ReceiptReuseRejected"
  // Customer payment finance approval (maker-checker)
  | "PaymentPendingApproval"
  | "PaymentApproved"
  | "PaymentRejected"
  | "BroadcastMessage";

export type NotificationCategory = "Informational" | "ActionRequired";
export type EmailDispatchStatus = "Pending" | "Sent" | "Failed" | "Suppressed";

export interface NotificationDto {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  body: string;
  linkUrl: string;
  sourceEntityType: string;
  sourceEntityId: string;
  isRead: boolean;
  readAt: string | null;
  isArchived: boolean;
  archivedAt: string | null;
  emailStatus: EmailDispatchStatus;
  createdAt: string;
}

export interface NotificationListResultDto {
  items: NotificationDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  unreadCount: number;
}

export interface NotificationListQuery {
  pageNumber?: number;
  pageSize?: number;
  isRead?: boolean | null;
  includeArchived?: boolean;
  types?: NotificationType[];
  sourceEntityTypes?: string[];
  createdFrom?: string;
  createdTo?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string | null;
  errorCode: string | null;
  data: T;
}

const unwrap = <T>(payload: ApiResponse<T> | T): T => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in (payload as Record<string, unknown>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

export const notificationsApi = {
  getUnreadCount: async (): Promise<number> => {
    const { data } = await apiClient.get<ApiResponse<{ unreadCount: number }>>(
      "/notifications/unread-count"
    );
    return unwrap(data).unreadCount ?? 0;
  },

  list: async (query: NotificationListQuery = {}): Promise<NotificationListResultDto> => {
    const params = new URLSearchParams();
    if (query.pageNumber != null) params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
    if (query.isRead != null) params.set("isRead", String(query.isRead));
    if (query.includeArchived != null)
      params.set("includeArchived", String(query.includeArchived));
    query.types?.forEach((t) => params.append("types", t));
    query.sourceEntityTypes?.forEach((s) => params.append("sourceEntityTypes", s));
    if (query.createdFrom) params.set("createdFrom", query.createdFrom);
    if (query.createdTo) params.set("createdTo", query.createdTo);

    const { data } = await apiClient.get<ApiResponse<NotificationListResultDto>>(
      `/notifications?${params.toString()}`
    );
    return unwrap(data);
  },

  markRead: async (id: string): Promise<NotificationDto> => {
    const { data } = await apiClient.post<ApiResponse<NotificationDto>>(
      `/notifications/${id}/read`
    );
    return unwrap(data);
  },

  markAllRead: async (): Promise<number> => {
    const { data } = await apiClient.post<ApiResponse<{ markedCount: number }>>(
      "/notifications/read-all"
    );
    return unwrap(data).markedCount ?? 0;
  },
};
